import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { InviteGigsafeUserPayload } from 'src/interfaces/gigsafeUser'
import { InviteGigsafeUserFields } from './InviteGigsafeUserFields'
import { InviteGigsafeUserSubmitButton } from './InviteGigsafeUserSubmitButton'
import { inviteGigsafeUserFormValidator } from './InviteGigsafeUserForm.validator'
import { mapInviteFormToPayload } from './InviteGigsafeUserForm.mapper'
import type { InviteGigsafeUserFormValues } from './InviteGigsafeUserForm.interface'

const FORM_ID = 'invite-gigsafe-user-form'

const INITIAL_VALUES: InviteGigsafeUserFormValues = {
  email: '',
  firstName: '',
  lastName: '',
  roleId: '',
  // Checked by default, matching Retool.
  allMarkets: true,
  marketIds: [],
}

interface Props {
  isOpen: boolean
  onClose(): void
  onSubmit(payload: Omit<InviteGigsafeUserPayload, 'partnerId'>): void
}

/**
 * Inviting is a one-off action rather than a standing panel, so it opens from the page header's
 * CTA as a form modal — the same shape as "New subscription" on Webhooks.
 *
 * `Formik` wraps the `CommonModal` so the footer's submit, which MUI portals out of the `<Form>`
 * element, still reaches the form through context.
 */
const InviteGigsafeUserModalComponent = ({ isOpen, onClose, onSubmit }: Props) => {
  const handleSubmit = useCallback(
    (values: InviteGigsafeUserFormValues) => {
      onSubmit(mapInviteFormToPayload(values))
    },
    [onSubmit],
  )

  if (!isOpen) {
    // Unmounting resets Formik, so the next open starts from a clean form.
    return null
  }

  return (
    <Formik<InviteGigsafeUserFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={inviteGigsafeUserFormValidator}
      onSubmit={handleSubmit}
    >
      <CommonModal
        isOpen={isOpen}
        title="Invite user"
        text="They get an email invite. The role and markets chosen here decide what they can see once they accept."
        onClose={onClose}
        className={tw`max-w-[560px]`}
        buttons={
          <Fragment>
            <Button size="small" color="grey" onClick={onClose}>
              Cancel
            </Button>
            <InviteGigsafeUserSubmitButton formId={FORM_ID} />
          </Fragment>
        }
      >
        <Form id={FORM_ID} data-testid="invite-gigsafe-user-form">
          <InviteGigsafeUserFields />
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const InviteGigsafeUserModal = memo(InviteGigsafeUserModalComponent)
